import { Injectable, computed, inject, signal } from '@angular/core';
import { ChannelDto, MetaDto, OrganizationDetailDto, OrganizationDto } from '@api/organization';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { getDarkMode, getDownloadComplete, getDownloadContent, getDownloadCount, getDownloadProgress,
         getTextSize, State, getDynamicContentUrl, getOrganization, getOrganizationDetail, getIsInhouse,
         getDisableFavorites, getDisableShare, channels, channelSelected, meta, userId,
         channelsUnlocked,
         getAcceptedEula,
         getReadInstructions,
         getSelectedName,
         getDoneActivities,
         getAvatarXp,
         getAvatarLevel,
         getVisititedLexiconIds,
         getFinishAnimationShown,
         getChildren,
         getSelectedChildId,
         getFirstStart} from '../ngrx';
import { setTextSize, setDownloadContent, setDarkMode, setDownloadProgress, setOrganization, setOrganizationDetail,
         setDynamicContentUrl, setIsInhouse, setDisableShare,
         setDisableFavorites,
         setLanguage,
         setDownloadCount,
         setDownloadComplete,
         setChannels,
         selectChannel,
         setMeta,
         setUserId,
         addUnlockedChannel,
         setSelectedName,
         setAcceptedEula,
         setReadInstructions,
         addDoneActivity,
         increaseAvatarXp,
         setAvatarLevel,
         addVisitedLexiconId,
         setFinishAnimationShown,
         addChild,
         setSelectedChildId,
         removeChild,
         setFirstStart} from '../ngrx/app.actions';
import { SwUpdate } from '@angular/service-worker';
import { map, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { appConfig } from '@assets/config/app.config';
import { ChannelIdToCode } from '@app/shared/models/channel-to-code';
import { toSignal } from '@angular/core/rxjs-interop';
import { Child } from '@app/shared/models/child';
import { Goal } from '@app/shared/models/form-profile';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private readonly appState$ = inject<Store<State>>(Store);
  private readonly swUpdate = inject(SwUpdate);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  private readonly acceptedEula$: Observable<boolean> = this.appState$.pipe(select(getAcceptedEula));
  private readonly readInstructions$: Observable<boolean> = this.appState$.pipe(select(getReadInstructions));
  private readonly children$: Observable<Child[]> = this.appState$.pipe(select(getChildren));
  private readonly selectedChildId$: Observable<string> = this.appState$.pipe(select(getSelectedChildId));
  private readonly doneActivities$: Observable<string[]> = this.appState$.pipe(select(getDoneActivities));

  public readonly dynamicContentUrl$: Observable<string> = this.appState$.pipe(select(getDynamicContentUrl));
  public readonly textSize$: Observable<string> = this.appState$.pipe(select(getTextSize));
  public readonly downloadContent$: Observable<boolean> = this.appState$.pipe(select(getDownloadContent));
  public readonly darkMode$: Observable<boolean> = this.appState$.pipe(select(getDarkMode));
  public readonly downloadComplete$: Observable<boolean> = this.appState$.pipe(select(getDownloadComplete));
  public readonly downloadProgress$: Observable<number> = this.appState$.pipe(select(getDownloadProgress));
  public readonly downloadCount$: Observable<number> = this.appState$.pipe(select(getDownloadCount));
  public readonly organization$: Observable<OrganizationDto> = this.appState$.pipe(select(getOrganization));
  public readonly isInhouse$: Observable<boolean> = this.appState$.pipe(select(getIsInhouse));
  public readonly disableFavorites$: Observable<boolean> = this.appState$.pipe(select(getDisableFavorites));
  public readonly disableShare$: Observable<boolean> = this.appState$.pipe(select(getDisableShare));
  public readonly channels$: Observable<ChannelDto[]> = this.appState$.pipe(select(channels));
  public readonly channelSelected$: Observable<ChannelDto> = this.appState$.pipe(select(channelSelected));
  public readonly channelsUnlocked$: Observable<ChannelIdToCode[]> = this.appState$.pipe(select(channelsUnlocked));
  public readonly meta$: Observable<MetaDto> = this.appState$.pipe(select(meta));
  public readonly userId$: Observable<string> = this.appState$.pipe(select(userId));
  public readonly selectedName$: Observable<string> = this.appState$.pipe(select(getSelectedName));
  public readonly avatarXp$: Observable<number> = this.appState$.pipe(select(getAvatarXp));
  public readonly avatarLevel$: Observable<number> = this.appState$.pipe(select(getAvatarLevel));
  public readonly visitedLexiconIds$: Observable<string[]> = this.appState$.pipe(select(getVisititedLexiconIds));
  public readonly finishAnimationShown$: Observable<boolean> = this.appState$.pipe(select(getFinishAnimationShown));
  public readonly isFirstStart$: Observable<boolean> = this.appState$.pipe(select(getFirstStart));
  public readonly organizationDetail$: Observable<OrganizationDetailDto> = this.appState$.pipe(
    select(getOrganizationDetail)
  );

  public readonly acceptedEulaSignal = toSignal(this.acceptedEula$);
  public readonly readInstructionsSignal = toSignal(this.readInstructions$);
  public readonly childrenSignal = toSignal(this.children$);
  public readonly selectedChildIdSignal = toSignal(this.selectedChildId$);
  public readonly firstStartSignal = toSignal(this.isFirstStart$);
  public readonly visitedLexiconIdsSignal = toSignal(this.visitedLexiconIds$);
  public readonly dynamicContentUrlSignal = toSignal(this.dynamicContentUrl$);
  public readonly doneActivitiesSignal = toSignal(this.doneActivities$);

  public selectedChild = computed(() => {
    return this.childrenSignal().find(child => child.id === this.selectedChildIdSignal());
  });

  public readonly showAllTipsSignal = signal(false);

  get pwaIsInstalled() {
    return window?.matchMedia('(display-mode: standalone)')?.matches;
  }

  setFinishAnimationShown(finishAnimationShown: boolean) {
    this.appState$.dispatch(setFinishAnimationShown({finishAnimationShown}));
  }

  setTextSize(textSize: string) {
    this.appState$.dispatch(setTextSize({textSize}));
  }

  setDownloadContent(downloadContent: boolean) {
    this.appState$.dispatch(setDownloadContent({downloadContent}));
  }

  setDarkMode(darkMode: boolean) {
    this.appState$.dispatch(setDarkMode({darkMode}));
  }

  setDownloadProgress(progress: number, count: number) {
    this.appState$.dispatch(setDownloadProgress({progress, count}));
  }

  setDownloadCount(count: number) {
    this.appState$.dispatch(setDownloadCount({count}));
  }

  setDownloadComplete(complete: boolean) {
    this.appState$.dispatch(setDownloadComplete({complete}));
  }

  setDynamicContentUrl(contentUrl: string) {
    this.appState$.dispatch(setDynamicContentUrl({contentUrl}));
  }

  setLanguage(language: string) {
    this.translate.use(language);
    this.appState$.dispatch(setLanguage({language}));
  }

  setOrganization(organization: OrganizationDto) {
    this.appState$.dispatch(setOrganization({organization}));
  }

  setOrganizationDetail(organizationDetail: OrganizationDetailDto) {
    this.appState$.dispatch(setOrganizationDetail({organizationDetail}));
  }

  setIsInhouse(isInhouse: boolean) {
    this.appState$.dispatch(setIsInhouse({isInhouse}));
  }

  setDisableFavorites(disableFavorites: boolean) {
    this.appState$.dispatch(setDisableFavorites({disableFavorites}));
  }

  setDisableShare(disableShare: boolean) {
    this.appState$.dispatch(setDisableShare({disableShare}));
  }

  updatePwa() {
    if (this.swUpdate.isEnabled && navigator.onLine) {
      this.swUpdate.versionUpdates
        .subscribe((event) => {
          if (event.type === "VERSION_READY") {
            location.reload();
          }
        });
    }
  }

  navigateWithUrlSuffix(url: string) {
    this.router.navigate([ `${url}` ] );
  }

  // Stuff that also needs to be filled if offline or online
  fillOrganizationFields(organization: OrganizationDto) {
    if (organization?.languages && organization.languages.length > 0) {
      this.translate.addLangs(organization.languages);
    } else {
      this.translate.addLangs(appConfig.language.locales);
    }
  }

  setChannels(channels: ChannelDto[]) {
    this.appState$.dispatch(setChannels({channels}));
  }

  selectChannel(channel: ChannelDto) {
    this.appState$.dispatch(selectChannel({channel}));
  }

  setMeta(meta: MetaDto) {
    this.appState$.dispatch(setMeta({meta}));
  }

  setUserId(userId: string) {
    this.appState$.dispatch(setUserId({userId}));
  }

  addUnlockedChannel(channelId: string, code: string) {
    this.appState$.dispatch(addUnlockedChannel({channelId, code}));
  }

  setSelectedName(name: string) {
    this.appState$.dispatch(setSelectedName({name}));
  }

  setAcceptedEula(acceptedEula: boolean) {
    this.appState$.dispatch(setAcceptedEula({acceptedEula}));
  }

  setReadInstructions(readInstructions: boolean) {
    this.appState$.dispatch(setReadInstructions({readInstructions}));
  }

  addDoneActivity(stationId: string) {
    this.appState$.dispatch(addDoneActivity({stationId}));
  }

  increaseAvatarXp(xp: number) {
    this.appState$.dispatch(increaseAvatarXp({xp}));
    this.updateAvatarLevel();
  }

  private getXpCapForLevel(level: number) {
    const levelCaps = [150, 400, 800, 1400];
    return levelCaps[level];
  }

  getLevelForXp(xp: number): number {
    if (xp >= 0 && xp < this.getXpCapForLevel(0)) {
      return 0;
    } else if (xp >= this.getXpCapForLevel(0) && xp < this.getXpCapForLevel(1)) {
      return 1;
    } else if (xp >= this.getXpCapForLevel(1) && xp < this.getXpCapForLevel(2)) {
      return 2;
    } else if (xp >= this.getXpCapForLevel(2) && xp < this.getXpCapForLevel(3)) {
      return 3;
    } else if (xp >= this.getXpCapForLevel(3)) {
      return 4;
    }
  }

  setAvatarLevel(avatarLevel: number) {
    this.appState$.dispatch(setAvatarLevel({avatarLevel}));
  }

  updateAvatarLevel() {
    combineLatest([this.avatarLevel$, this.avatarXp$])
      .pipe(
        take(1)
      ).subscribe(([level, xp]) => {
        const newLevel = this.getLevelForXp(xp);
        if (newLevel > level) {
          this.setAvatarLevel(newLevel);
        }
      });
  }

  get avatarProgressInLevel$(): Observable<number> {
    return combineLatest([this.avatarLevel$, this.avatarXp$])
      .pipe(
        map(([level, xp]) => {
          const totalXpForPreviousLevel = level == 0 ? 0 : this.getXpCapForLevel(level - 1);
          const totalXpForNextLevel = this.getXpCapForLevel(level);
          return (1 - ((totalXpForNextLevel - xp) / (totalXpForNextLevel - totalXpForPreviousLevel))) * 100;
        }
        )
      )
  }

  addVisitedLexiconId(lexiconId: string) {
    this.appState$.dispatch(addVisitedLexiconId({lexiconId}));
  }

  public addChild(child: Child) {
    this.appState$.dispatch(addChild({child}));
  }

  public selectChild(selectedChildId: string) {
    this.showAllTipsSignal.set(false);
    this.appState$.dispatch(setSelectedChildId({selectedChildId}));
  }

  public removeChild(childId: string) {
    this.appState$.dispatch(removeChild({childId}));
  }

  public changeMainGoalOfSelectedChild(goalToUpdate: Goal) {
    const updatedChild = {...this.selectedChild(), goal: goalToUpdate};
    this.addChild(updatedChild);
  }

  public getPageTitle(routerUrl: string): string | undefined {
    if (routerUrl.includes('templates')) {
      return 'Vorlagen';
    } else if (routerUrl.includes('lexicon')) {
      return 'Wissen A-Z';
    } else if (routerUrl.includes('contact')) {
      return 'Auskunft';
    }  else if (routerUrl.includes('statistic')) {
      return 'Meine Bewertungen';
    } else if (routerUrl.includes('personalization') || this.router.url.includes('profile')) {
      return 'Profil';
    } else if (routerUrl.includes('settings')) {
      return 'Einstellungen';
    } else if (routerUrl.includes('child-admin')) {
      return 'Kinder';
    } else if (routerUrl.includes('favorites')) {
      return 'Merkliste';
    } else {
      return undefined;
    }
  }

  public setFirstStart(firstStart: boolean) {
    this.appState$.dispatch(setFirstStart({firstStart}));
  }

  public showDialogIfFirstStart() {
    console.log('showDialogIfFirstStart', this.firstStartSignal());
    if (this.firstStartSignal() === true) {
      document.getElementById('popover-first-instruction')?.showPopover();
      this.setFirstStart(false);
    }
  }

}
